import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";

interface LocationQueueProps {
  collectionId: string;
  selectedLocationId: string | null;
  onSelect: (locationId: string) => void;
}

export function LocationQueue({
  collectionId,
  selectedLocationId,
  onSelect,
}: LocationQueueProps) {
  const { data: locations, isLoading } = trpc.physicalMapping.list.useQuery(
    { collectionId },
    { enabled: !!collectionId },
  );

  if (isLoading) {
    return (
      <div className="p-3 space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!locations || locations.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        No locations in this collection.
      </div>
    );
  }

  return (
    <div className="divide-y">
      {locations.map((loc: any) => {
        const total = loc.pil_total ?? 0;
        const ready = loc.pil_done ?? 0;
        const isActive = loc.id === selectedLocationId;
        return (
          <button
            key={loc.id}
            type="button"
            onClick={() => onSelect(loc.id)}
            className={cn(
              "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors",
              isActive && "bg-muted",
            )}
          >
            <div className="flex-1 min-w-0">
              <div
                className={cn(
                  "text-sm truncate",
                  isActive ? "font-semibold" : "font-medium",
                )}
              >
                {loc.name}
              </div>
              {loc.address && (
                <div className="text-xs text-muted-foreground truncate">
                  {loc.address}
                </div>
              )}
            </div>
            <Badge
              variant="outline"
              className="text-[10px] font-medium tabular-nums shrink-0"
              title={`${ready} PIL${ready === 1 ? "" : "s"} ready to review of ${total} total`}
            >
              {ready}/{total}
            </Badge>
            <ChevronRight className="size-3.5 text-muted-foreground shrink-0" />
          </button>
        );
      })}
    </div>
  );
}
